import type { ReactNode } from 'react'
import { Badge, Menu, TooltipTextOverflow } from '@systemeio/components'
import { DotsVerticalIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { useActivityRelativeDate } from 'shared/hooks/use-community-relative-date'
import useUser, { isDomainOwner, isWsAdmin, isWsAssistant } from 'shared/hooks/use-user'
import { MemberRoleEnum, getReadableMemberRole } from 'modules/community/enums/member-enum'
import type {
  BannedMemberInterface,
  MemberInterface,
} from 'modules/community/types/member-interface'

interface MemberShortProps {
  member: BannedMemberInterface | MemberInterface
  actionMenuItems?: { label: string; onClick: () => void }[]
}

export const MemberShort = ({ member, actionMenuItems }: MemberShortProps): ReactNode => {
  const { user } = useUser()
  const { t } = useTranslation()

  const isOwner = user && isDomainOwner(user)
  const isMemberInterface = 'createdAt' in member
  const bannedAt = 'userBannedAt' in member ? member.userBannedAt : null
  // TODO: Rewrite this new Date call
  const memberJoinedDate = new Date(
    isMemberInterface ? member.createdAt : (member.memberCreatedAt ?? member.userBannedAt),
  )
  const joinedRelative = useActivityRelativeDate(memberJoinedDate)
  const bannedRelative = useActivityRelativeDate(bannedAt)
  const memberEmail = user && (isOwner || isWsAssistant(user) || isWsAdmin(user)) && member.email
  const showActionMenu =
    member.role !== MemberRoleEnum.Owner && member.role !== null && !!actionMenuItems?.length

  return (
    <div className="group relative flex size-full flex-col items-center overflow-hidden rounded-lg bg-bg-primary px-5 py-7 lg:px-7 lg:py-9">
      {showActionMenu && (
        <div className="absolute top-3 right-3">
          <div className="invisible relative flex items-center group-hover:visible">
            <Menu
              trigger={
                <span className="flex items-center">
                  <DotsVerticalIcon className="size-5" />
                </span>
              }
              triggerProps={{ className: 'flex items-center' }}
              items={actionMenuItems.map(({ label, onClick }) => ({
                id: label,
                label,
                onSelect: onClick,
              }))}
            />
          </div>
        </div>
      )}
      <TooltipTextOverflow
        className="block w-full truncate text-center text-xl font-medium text-text-primary"
        tooltipContent={member.displayName ?? ''}
      >
        {member.displayName ?? ''}
      </TooltipTextOverflow>
      <img
        src={member.avatarUrl || '/default_post_author_avatar.webp'}
        alt={isMemberInterface ? member?.firstName : (member.displayName ?? '')}
        className="mx-auto my-2 block size-14 rounded-full sm:mx-0 sm:shrink-0 lg:size-20"
      />

      <div className="flex w-full flex-col items-center">
        {memberEmail && member.email && (
          <TooltipTextOverflow
            className="mt-0.5 block w-full truncate text-center text-sm text-text-primary"
            tooltipContent={member.email}
          >
            {member.email}
          </TooltipTextOverflow>
        )}
        <div className="mt-1">
          {member.role && <Badge color="blue">{t(getReadableMemberRole(member.role))}</Badge>}
        </div>
        <span className="mt-2 text-sm font-normal text-text-tertiary">
          {t('members.list.member.joined')} {joinedRelative}
        </span>
        {bannedAt != null && (
          <p className="mt-1 text-sm text-text-error">
            {t('members.banned_days')} {bannedRelative}
          </p>
        )}
      </div>
    </div>
  )
}
